"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Sun, Moon, Eye } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function MoonCardPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-indigo-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Rider-Waite</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">Majör Arkana</span>
            <span className="text-indigo-500">/</span>
            <span className="text-sm">The Moon</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            AXTROLOG Nedir
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Rider-Waite
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana"
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            Majör Arkana
          </Link>
          <span className="text-slate-500 mx-2">/</span>
          <span className="text-slate-300">The Moon</span>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-indigo-500/20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/rider-waite-moon.svg"
                  alt="The Moon Tarot Card"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-700 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XVIII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">The Moon</h1>
                  <p className="text-xl text-indigo-400">Ay / Yanılsama</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Yanılsama, bilinçsiz korkular ve gizli gerçeklerin kartı. İçsel yolculuk,
                sezgisel bilgi ve gölge çalışmasını temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm">Yanılsama</span>
                <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm">Sezgi</span>
                <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm">Korkular</span>
                <span className="px-3 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-sm">Gizem</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Sun className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sezgisel Bilgi</h3>
                  <p className="text-slate-400 text-sm">İçsel bilgelik, psişik yetenekler ve rüya mesajları.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bilinçsiz Keşfi</h3>
                  <p className="text-slate-400 text-sm">Gizli duyguları ve bastırılan anıları ortaya çıkarma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gölge Çalışması</h3>
                  <p className="text-slate-400 text-sm">Karanlık yanlarla yüzleşme ve entegre etme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Feminen Enerji</h3>
                  <p className="text-slate-400 text-sm">Ana tanrıça, döngüsellik ve doğal ritimler.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Moon className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yanılsamadan Kurtulma</h3>
                  <p className="text-slate-400 text-sm">Gerçeği görme, aldanmışlıktan çıkma.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Sezgiyi Görmezden Gelme</h3>
                  <p className="text-slate-400 text-sm">İçsel sesi dinlememe, mantığa aşırı güvenme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Korkularla Boğuşma</h3>
                  <p className="text-slate-400 text-sm">İrrasyonel endişeler, paranoyia ve kaygı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Gizli Düşmanlar</h3>
                  <p className="text-slate-400 text-sm">Aldatma, manipülasyon ve gizli entrikalar.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Sembolik Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <Moon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Ay</h3>
              <p className="text-slate-400 text-sm">Bilinçsiz zihin, döngüler ve yanılsama. Feminen enerji.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🐺</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Köpek ve Kurt</h3>
              <p className="text-slate-400 text-sm">Evcil ve vahşi doğa, bilinçli ve bilinçsiz korkular.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🦀</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kerevit</h3>
              <p className="text-slate-400 text-sm">Bilinçsizden çıkan içgüdüler, evrimsel hafıza.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🗼</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İki Kule</h3>
              <p className="text-slate-400 text-sm">Bilinçli zihnin sınırları, bilinen dünya.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">💧</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yansıyan Damla</h3>
              <p className="text-slate-400 text-sm">Ay ışığının yanılsaması, gerçeğin çarpıtılması.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-brown-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">🛤️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kıvrımlı Yol</h3>
              <p className="text-slate-400 text-sm">Belirsiz gelecek, dolambaçlı ruhsal yolculuk.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Gizli duygular ve arzular</li>
                <li>• Belirsizlik ve karmaşa</li>
                <li>• Aldatma veya yanılsama</li>
                <li>• Derin bağlantı arayışı</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Belirsizlik ve karmaşa</li>
                <li>• Gizli bilgiler</li>
                <li>• Yaratıcı ve sanatsal alanlar</li>
                <li>• Sezgisel kararlar</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Sun className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Bilinçsiz keşfi</li>
                <li>• Rüya analizi</li>
                <li>• Gölge çalışması</li>
                <li>• Psişik gelişim</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-400" />
                Ruh Hali ve Farkındalık
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Duygusal dalgalanmalar</li>
                <li>• Kaygı ve endişeler</li>
                <li>• İçsel yolculuk</li>
                <li>• Sezgisel farkındalık</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
          <p className="text-amber-100 leading-relaxed">
            Bu kart anlamları geleneksel tarot yorumlarına dayanmaktadır ve sadece eğlence ve kişisel farkındalık amaçlıdır.
            Yaşamsal kararlar için uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/star"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            The Star
          </Link>

          <Link
            href="/about/tarot/systems/rider-waite/major-arcana/sun"
            className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
          >
            The Sun
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}